import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Badge, Card, PageHeader } from '@/components/ui/primitives';
import { EmptyState, ErrorState, LoadingState } from '@/components/DataState';
import { fetchRevenueAttribution } from '@/data/sources-salesb';
import { formatCurrency } from '@/lib/utils';
import type { RevenueAttributionRow } from '@/types/salesb';

/* /sales/revenue-attribution — Revenue Attribution (W2b). THE MOAT SURFACE:
 * which channels/actions produce REAL revenue, joining sales events →
 * opportunities.
 *
 * HONESTY + SAFETY INVARIANTS (W2b contract):
 *  - FREE + read-only: joins stored events and opportunities, NEVER a send,
 *    never a charge. Safe to AUTO-LOAD on mount.
 *  - HONEST NULLS ARE THE POINT: won_value / revenue_per_lead stay null until a
 *    booking/opportunity is actually Won — rendered as an explicit
 *    "No won opportunity yet", NEVER as £0 or an invented number. A channel
 *    with real activity but no win must read as unproven, not unprofitable.
 *  - Source-attributed ("bksesh_sales_lead_events+bc_crm_opportunities"); the
 *    contract's own honesty note is surfaced verbatim.
 *  - Uses the existing sales-page table style — does NOT import the SEO-scoped
 *    SortableTable (owned by W2a-FE this wave).
 */

const SOURCE_TAG = 'Source: Events → opportunities (bksesh_sales_lead_events + bc_crm_opportunities)';

const WINDOW_OPTIONS = [30, 90, 180, 365];

function formatInt(n: number): string {
  return n.toLocaleString('en-GB');
}

/** Won revenue cell — honest null (no win yet) is explicit, never £0. */
function wonValueCell(row: RevenueAttributionRow) {
  if (row.won_value === null) {
    return <span className="text-xs italic text-text-tertiary">No won opportunity yet</span>;
  }
  return (
    <span className="font-mono text-sm font-semibold text-text-primary" data-tabular>
      {formatCurrency(row.won_value, row.currency)}
    </span>
  );
}

/** Revenue-per-lead cell — null stays an em-dash (nothing to divide yet). */
function revenuePerLeadCell(row: RevenueAttributionRow) {
  if (row.revenue_per_lead === null) {
    return <span className="text-text-tertiary">—</span>;
  }
  return (
    <span className="font-mono text-sm text-text-primary" data-tabular>
      {formatCurrency(row.revenue_per_lead, row.currency)}
    </span>
  );
}

export function RevenueAttributionPage() {
  const [window, setWindow] = useState(90);

  // FREE read-only surface — auto-loads. No send, no provider call, no charge.
  const { data, isPending, error, refetch } = useQuery({
    queryKey: ['crm', 'revenue-attribution', window],
    queryFn: () => fetchRevenueAttribution(window),
  });

  const rows = data?.attribution ?? [];
  const source = data?.source ?? 'bksesh_sales_lead_events+bc_crm_opportunities';
  // Channels that have actually produced won revenue — the moat, quantified.
  const provenChannels = rows.filter((r) => r.won_value !== null).length;

  const windowSelector = (
    <label className="flex items-center gap-2">
      <span className="text-xs font-medium text-text-secondary">Window</span>
      <select
        value={window}
        onChange={(e) => setWindow(Number(e.target.value))}
        className="h-control rounded-md border border-border-default bg-canvas px-3 text-sm text-text-primary outline-none focus:border-ai focus:ring-2 focus:ring-ai/30"
        aria-label="Attribution window (days)"
      >
        {WINDOW_OPTIONS.map((d) => (
          <option key={d} value={d}>
            Last {d} days
          </option>
        ))}
      </select>
    </label>
  );

  return (
    <div className="space-y-6">
      <PageHeader
        title="Revenue Attribution"
        subtitle="Which channels produce real revenue — sales events joined to won opportunities. Revenue stays honest: null until a booking is actually Won."
        actions={windowSelector}
      />

      {isPending ? (
        <LoadingState rows={5} />
      ) : error ? (
        <ErrorState error={error} onRetry={() => void refetch()} />
      ) : (
        <Card className="p-4">
          <div className="flex flex-wrap items-center gap-2">
            <Badge tone="neutral">{SOURCE_TAG}</Badge>
            <span className="text-xs text-text-tertiary">
              {rows.length
                ? `${formatInt(rows.length)} ${rows.length === 1 ? 'channel' : 'channels'} · ${formatInt(provenChannels)} with won revenue · last ${data.window} days`
                : `No attributed activity in the last ${data.window} days`}
            </span>
          </div>

          <div className="mt-4">
            {rows.length === 0 ? (
              <EmptyState
                title="No attribution yet"
                teach="Channels appear here once outreach events are recorded and opportunities open against them. Won revenue is attributed only when a booking or opportunity is actually Won — until then a channel shows its activity honestly, with no revenue claimed."
              />
            ) : (
              <div className="overflow-hidden rounded-lg border border-border-subtle bg-surface">
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[760px] border-collapse">
                    <thead>
                      <tr className="border-b border-border-subtle bg-inset/50 text-left text-[11px] uppercase tracking-wide text-text-tertiary">
                        <th className="px-3 py-2 font-semibold">Channel</th>
                        <th className="px-3 py-2 text-right font-semibold">Leads</th>
                        <th className="px-3 py-2 text-right font-semibold">Replies</th>
                        <th className="px-3 py-2 text-right font-semibold">Opportunities</th>
                        <th className="px-3 py-2 text-right font-semibold">Won revenue</th>
                        <th className="px-3 py-2 text-right font-semibold">Revenue / lead</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((r, i) => (
                        <tr
                          key={`${r.source_channel}-${i}`}
                          className="border-b border-border-subtle last:border-0 hover:bg-hover"
                        >
                          <td className="px-3 py-2.5 text-sm font-medium text-text-primary">
                            {r.source_channel}
                          </td>
                          <td className="px-3 py-2.5 text-right font-mono text-sm text-text-secondary" data-tabular>
                            {formatInt(r.leads)}
                          </td>
                          <td className="px-3 py-2.5 text-right font-mono text-sm text-text-secondary" data-tabular>
                            {formatInt(r.replies)}
                          </td>
                          <td className="px-3 py-2.5 text-right font-mono text-sm text-text-secondary" data-tabular>
                            {formatInt(r.opportunities)}
                          </td>
                          <td className="px-3 py-2.5 text-right">{wonValueCell(r)}</td>
                          <td className="px-3 py-2.5 text-right">{revenuePerLeadCell(r)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="border-t border-border-subtle px-3 py-2 text-[11px] text-text-tertiary">
                  Read-only from {source} · {data.note}
                </div>
              </div>
            )}
          </div>
        </Card>
      )}
    </div>
  );
}
